import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Container, Button, ButtonGroup, useTheme, useMediaQuery, Card, CardContent, CardActions, Stack, Divider, Box, Chip, Link as MuiLink, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, MenuItem, CircularProgress, IconButton, Tooltip, Autocomplete, Alert } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarQuickFilter, GridColumnVisibilityModel } from '@mui/x-data-grid';
import { useApplicationContext } from 'ApplicationContext';
import { DeviceQueryModel, DeviceSearchHit, GetSupportCodeData, DeviceStatusEnum, SetDeviceStatusModel, CreateDeviceModel, EUserGroup, GetHostSystemData, AssignSupportCodeModel, UpdateDeviceHostSystemModel, HostIdResponse } from 'apiClient/Api';
import { useSemanticSearch } from 'Hooks/useSemanticSearch';
import { SemanticSearchBar } from 'Components/SemanticSearchBar';
import { SearchScoreChip } from 'Components/SearchScoreChip';
import { Link, useNavigate, useParams } from 'react-router';
import { useDocumentTitle } from 'Hooks/useDocumentTitle';
import { Block, Delete, Link as LinkIcon, Edit, CheckCircle, VpnKey, Warning, CheckCircleOutlined as CheckCircleOutline, ErrorOutlined as ErrorOutline, Upload, ContentCopy, LinkOff, Fingerprint } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileCode } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import { DeviceLicenseDecodeDialog } from './DeviceLicenseDecodeDialog';
import { DeviceLicensePublicKeyDialog } from './DeviceLicensePublicKeyDialog';

/**
 * DeviceManagement component
 * @returns {JSX.Element} The DeviceManagement page
 */
const DeviceManagement: React.FC = (): JSX.Element => {

  useDocumentTitle("base.pages.deviceManagement")
  const context = useApplicationContext();
  const controller = context.api.device;
  const supportCodeController = context.api.supportCode;

  const { t } = useTranslation();
  const [devices, setDevices] = React.useState<DeviceQueryModel[]>([]);
  const [supportCode, setSupportCode] = React.useState<GetSupportCodeData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [registerDialogOpen, setRegisterDialogOpen] = useState<boolean>(false);
  const [registerData, setRegisterData] = useState<CreateDeviceModel & { supportCodeId?: string }>({ updatesMonths: 12 });
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [customerSupportCodes, setCustomerSupportCodes] = useState<GetSupportCodeData[]>([]);
  const [customerSupportCodesLoading, setCustomerSupportCodesLoading] = useState<boolean>(false);
  const [registerLoading, setRegisterLoading] = useState<boolean>(false);
  const [hostSystems, setHostSystems] = useState<GetHostSystemData[]>([]);
  const [hostSystemsLoading, setHostSystemsLoading] = useState<boolean>(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState<boolean>(false);
  const [assignDeviceId, setAssignDeviceId] = useState<string | null>(null);
  const [supportCodes, setSupportCodes] = useState<GetSupportCodeData[]>([]);
  const [supportCodesLoading, setSupportCodesLoading] = useState<boolean>(false);
  const [supportSearch, setSupportSearch] = useState<string>('');
  const [selectedSupportCode, setSelectedSupportCode] = useState<GetSupportCodeData | null>(null);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [updateHostDialogOpen, setUpdateHostDialogOpen] = useState<boolean>(false);
  const [updateHostDeviceId, setUpdateHostDeviceId] = useState<string | null>(null);
  const [updateHostSystemId, setUpdateHostSystemId] = useState<string>('');
  const [updateHostError, setUpdateHostError] = useState<string | null>(null);
  const [decodeDialogOpen, setDecodeDialogOpen] = useState<boolean>(false);
  const [publicKeyDialogOpen, setPublicKeyDialogOpen] = useState<boolean>(false);
  const [hostIdCheckData, setHostIdCheckData] = useState<HostIdResponse | null>(null);
  const [hostIdCheckLoading, setHostIdCheckLoading] = useState<boolean>(false);
  const [hostIdCheckError, setHostIdCheckError] = useState<string | null>(null);
  const [hostIdInputValue, setHostIdInputValue] = useState<string>('');
  const [showHostIdCheck, setShowHostIdCheck] = useState<boolean>(false);
  const [hostIdCheckWaiting, setHostIdCheckWaiting] = useState<boolean>(false);
  const { supportId } = useParams<{ supportId?: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({ deviceId: false, customerDeviceId: true, hostSystemName: false, osVersion: false, updatesInfo: false });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDevelopment = (context.userData?.userGroup ?? EUserGroup.CUSTOMER) <= EUserGroup.DEVELOPMENT;
  const isAdmin = (context.userData?.userGroup ?? EUserGroup.CUSTOMER) <= EUserGroup.ADMIN;
  
  // Show deleted devices toggle (only for admins)
  const [showDeleted, setShowDeleted] = useState<boolean>(false);

  // Semantic search across devices
  const search = useSemanticSearch<DeviceSearchHit>({
    searchFn: async (query) => (await context.api.search.searchSearchDevices({ query, limit: 100 })).data,
    extractKey: (hit) => ({ key: hit.deviceId, score: hit.score }),
  });

  const displayedDevices = useMemo(() => {
    if (!search.hits) return devices;
    const hits = search.hits;
    return devices
      .filter(d => d.id && hits.has(d.id))
      .sort((a, b) => (hits.get(b.id!) ?? 0) - (hits.get(a.id!) ?? 0));
  }, [devices, search.hits]);
  
  // XML Upload states
  const [xmlUploadDialogOpen, setXmlUploadDialogOpen] = useState<boolean>(false);
  const [xmlUploadFile, setXmlUploadFile] = useState<File | null>(null);
  const [xmlUploadSupportCodeId, setXmlUploadSupportCodeId] = useState<string>('');
  const [xmlUploadCustomerDeviceId, setXmlUploadCustomerDeviceId] = useState<string>('');
  const [xmlUploadLoading, setXmlUploadLoading] = useState<boolean>(false);
  const [xmlUploadError, setXmlUploadError] = useState<string | null>(null);
  const xmlFileInputRef = React.useRef<HTMLInputElement>(null);

  // Permanent delete states
  const [permanentDeleteDialogOpen, setPermanentDeleteDialogOpen] = useState<boolean>(false);
  const [permanentDeleteDeviceId, setPermanentDeleteDeviceId] = useState<string | null>(null);
  const [permanentDeleteLoading, setPermanentDeleteLoading] = useState<boolean>(false);

  // Clone device states
  const [cloneDialogOpen, setCloneDialogOpen] = useState<boolean>(false);
  const [cloneDeviceId, setCloneDeviceId] = useState<string | null>(null);
  const [cloneHostId, setCloneHostId] = useState<string>('');
  const [cloneDeviceName, setCloneDeviceName] = useState<string>('');
  const [cloneLoading, setCloneLoading] = useState<boolean>(false);
  const [cloneError, setCloneError] = useState<string | null>(null);

  // Release HostID states
  const [releaseHostDialogOpen, setReleaseHostDialogOpen] = useState<boolean>(false);
  const [releaseHostDeviceId, setReleaseHostDeviceId] = useState<string | null>(null);
  const [releaseHostReason, setReleaseHostReason] = useState<string>('');
  const [releaseHostLoading, setReleaseHostLoading] = useState<boolean>(false);
  const [releaseHostError, setReleaseHostError] = useState<string | null>(null);

  // Assign Hardware-ID (customer one-time) states
  const [assignHostIdDialogOpen, setAssignHostIdDialogOpen] = useState<boolean>(false);
  const [assignHostIdDeviceId, setAssignHostIdDeviceId] = useState<string | null>(null);
  const [assignHostIdValue, setAssignHostIdValue] = useState<string>('');
  const [assignHostIdLoading, setAssignHostIdLoading] = useState<boolean>(false);
  const [assignHostIdError, setAssignHostIdError] = useState<string | null>(null);

  /**
   * Gets a stable row id for the data grid.
   * @param {DeviceQueryModel} row - The device row.
   * @returns {string} The row identifier.
   */
  const getDeviceRowId = (row: DeviceQueryModel): string => {
    return row.id ?? row.deviceId ?? `${row.hostSystemName ?? 'device'}-${row.supportCode ?? ''}`;
  };

  /**
   * Builds the link to a support contract view based on a support code value.
   * @param {string | null | undefined} supportCodeValue - The support code value to use for filtering.
   * @returns {string} The route to the support contract view.
   */
  const getSupportContractLink = (supportCodeValue?: string | null): string => {
    if (!supportCodeValue) {
      return '/deviceManagement';
    }

    return `/deviceManagement/${encodeURIComponent(supportCodeValue)}`;
  };

  /**
   * Opens the dialog to register a device.
   */
  const openRegisterDialog = (): void => {
    setRegisterData((prev) => ({
      ...prev,
      updatesMonths: prev.updatesMonths ?? 12,
    }));
    setRegisterDialogOpen(true);
    
    // Load support codes for customers
    if (!isAdmin) {
      loadCustomerSupportCodes();
    }
  };

  /**
   * Loads support codes available to the customer.
   */
  const loadCustomerSupportCodes = async (): Promise<void> => {
    setCustomerSupportCodesLoading(true);
    try {
      const response = await supportCodeController.supportCodeListSupportCodes();
      setCustomerSupportCodes(response.data ?? []);
    } catch (err) {
      console.error('Failed to load customer support codes', err);
    } finally {
      setCustomerSupportCodesLoading(false);
    }
  };

  /**
   * Resets the registration form state.
   */
  const resetRegisterForm = (): void => {
    setRegisterData({ updatesMonths: 12 });
    setRegisterError(null);
    setHostIdInputValue('');
    setHostIdCheckData(null);
    setHostIdCheckError(null);
    setShowHostIdCheck(false);
    setHostIdCheckWaiting(false);
  };

  /**
   * Checks the HostID validity using the API
   */
  const checkHostId = useCallback(async (hostId: string) => {
    if (!hostId || hostId.length < 5) {
      setShowHostIdCheck(false);
      return;
    }

    setHostIdCheckLoading(true);
    setHostIdCheckError(null);
    setShowHostIdCheck(true);
    try {
      const response = await context.api.api.hostIdCheckHostId(hostId);
      setHostIdCheckData(response.data ?? null);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error || err?.response?.data?.title || err?.message || t('error.fetchFailed');
      setHostIdCheckError(errorMessage);
      setHostIdCheckData(null);
    } finally {
      setHostIdCheckLoading(false);
    }
  }, [context.api.api, t]);

  /**
   * Closes the device registration dialog.
   */
  const closeRegisterDialog = (): void => {
    setRegisterDialogOpen(false);
    resetRegisterForm();
  };

  /**
   * Opens the dialog to assign a support code to a device.
   * @param {string | null | undefined} deviceId - The device identifier.
   */
  const openAssignDialog = (deviceId?: string | null): void => {
    if (!deviceId) {
      setError(t('error.fetchFailed'));
      return;
    }

    setAssignDeviceId(deviceId);
    setSupportSearch('');
    setSelectedSupportCode(null);
    setAssignError(null);
    setAssignDialogOpen(true);
  };

  /**
   * Closes the support code assignment dialog.
   */
  const closeAssignDialog = (): void => {
    setAssignDialogOpen(false);
    setAssignDeviceId(null);
    setSupportSearch('');
    setSelectedSupportCode(null);
    setAssignError(null);
  };

  /**
   * Opens the dialog to update a device host system.
   * @param {DeviceQueryModel} device - The selected device.
   */
  const openUpdateHostDialog = (device: DeviceQueryModel): void => {
    if (!device.id) {
      setError(t('error.fetchFailed'));
      return;
    }

    const matchedHostSystem = hostSystems.find((hostSystem) => hostSystem.name === device.hostSystemName);
    setUpdateHostDeviceId(device.id ?? null);
    setUpdateHostSystemId(matchedHostSystem?.id ?? '');
    setUpdateHostError(null);
    setUpdateHostDialogOpen(true);
  };

  /**
   * Closes the host system update dialog.
   */
  const closeUpdateHostDialog = (): void => {
    setUpdateHostDialogOpen(false);
    setUpdateHostDeviceId(null);
    setUpdateHostSystemId('');
    setUpdateHostError(null);
  };

  /**
   * Opens the license decode dialog.
   */
  const openDecodeDialog = (): void => {
    setDecodeDialogOpen(true);
  };

  /**
   * Closes the license decode dialog.
   */
  const closeDecodeDialog = (): void => {
    setDecodeDialogOpen(false);
  };

  /**
   * Opens the public key dialog.
   */
  const openPublicKeyDialog = (): void => {
    setPublicKeyDialogOpen(true);
  };

  /**
   * Closes the public key dialog.
   */
  const closePublicKeyDialog = (): void => {
    setPublicKeyDialogOpen(false);
  };


  /**
   * Opens the license detail page for a device.
   * @param {string | null | undefined} deviceId - The device identifier.
   */
  const openLicensePage = (deviceId?: string | null): void => {
    if (!deviceId) {
      setError(t('error.fetchFailed'));
      return;
    }

    navigate(`/device/${deviceId}`);
  };

  /**
   * Updates a field in the registration form.
   * @param {keyof CreateDeviceModel} field - The field to update.
   * @param {string} value - The new value for the field.
   */
  const handleRegisterFieldChange = (field: keyof CreateDeviceModel, value: string): void => {
    setRegisterData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * Updates a numeric field in the registration form.
   * @param {keyof CreateDeviceModel} field - The numeric field to update.
   * @param {string} value - The raw input value.
   */
  const handleRegisterNumberChange = (field: keyof CreateDeviceModel, value: string): void => {
    const parsedValue = value.trim() === '' ? undefined : Number(value);

    setRegisterData((prev) => ({
      ...prev,
      [field]: Number.isNaN(parsedValue) ? undefined : parsedValue,
    }));
  };

  /**
   * Creates a device as an administrator or customer.
   */
  const handleRegisterDevice = async (): Promise<void> => {
    // HostSystem is required for everyone
    if (!registerData.hostSystemId) {
      setRegisterError(t('deviceManagement.registerDevice.errors.hostSystemRequired'));
      return;
    }

    // Validation for customers
    if (!isAdmin) {
      if (!registerData.supportCodeId) {
        setRegisterError(t('deviceManagement.registerDevice.errors.supportCodeRequired'));
        return;
      }
      if (!registerData.customerDeviceId?.trim()) {
        setRegisterError(t('deviceManagement.registerDevice.errors.customerDeviceIdRequired'));
        return;
      }
    } else {
      // Validation for admins only
      if (!registerData.updatesMonths) {
        setRegisterError(t('deviceManagement.registerDevice.errors.updatesMonthsRequired'));
        return;
      }
    }

    setRegisterLoading(true);
    setRegisterError(null);

    try {
      await controller.deviceCreateDevice({
        name: registerData.name?.trim() || undefined,
        // Kunden dürfen beim Bestell-Flow keine Hardware-ID setzen.
        deviceId: isAdmin ? (registerData.deviceId?.trim() || undefined) : undefined,
        customerDeviceId: registerData.customerDeviceId?.trim() || undefined,
        hostSystemId: registerData.hostSystemId,
        updatesMonths: registerData.updatesMonths ?? undefined,
        supportCodeId: registerData.supportCodeId || undefined,
      });

      closeRegisterDialog();
      updateData();
    } catch (err: any) {
      const message = err?.response?.data || err?.message;
      setRegisterError(typeof message === 'string' && message.length > 0 ? message : t('error.fetchFailed'));
    } finally {
      setRegisterLoading(false);
    }
  };

  /**
   * Assigns a support code to the selected device.
   */
  const handleAssignSupportCode = async (): Promise<void> => {
    if (!assignDeviceId) {
      setAssignError(t('error.fetchFailed'));
      return;
    }

    const manualSupportCode = supportSearch.trim();
    const supportCodeId = selectedSupportCode?.id ?? manualSupportCode;

    if (!supportCodeId) {
      setAssignError(t('deviceManagement.assignSupportCode.errors.supportCodeRequired'));
      return;
    }

    try {
      let resolvedSupportCodeId = supportCodeId;

      if (!selectedSupportCode) {
        const response = await supportCodeController.supportCodeGetSupportCode(supportCodeId);
        resolvedSupportCodeId = response.data?.id ?? '';
      }

      if (!resolvedSupportCodeId) {
        setAssignError(t('deviceManagement.assignSupportCode.errors.supportCodeNotFound'));
        return;
      }

      const payload: AssignSupportCodeModel = { supportCodeId: resolvedSupportCodeId };
      await controller.deviceAssignSupportCode(assignDeviceId, payload);
      closeAssignDialog();
      updateData();
    } catch (err) {
      setAssignError(t('error.fetchFailed'));
    }
  };

  /**
   * Updates the host system for the selected device.
   */
  const handleUpdateHostSystem = async (): Promise<void> => {
    if (!updateHostDeviceId) {
      setUpdateHostError(t('error.fetchFailed'));
      return;
    }

    if (!updateHostSystemId) {
      setUpdateHostError(t('deviceManagement.updateHostSystem.errors.hostSystemRequired'));
      return;
    }

    try {
      const payload: UpdateDeviceHostSystemModel = { hostSystemId: updateHostSystemId };
      await controller.deviceUpdateDeviceHostSystem(updateHostDeviceId, payload);
      closeUpdateHostDialog();
      updateData();
    } catch (err) {
      setUpdateHostError(t('error.fetchFailed'));
    }
  };

  /**
   * Updates the data by fetching devices and support code if available
   */
  const updateData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const deviceData = showDeleted 
        ? await controller.deviceGetDeletedDevices({ supportCodeId: supportId })
        : await controller.deviceGetDevices({ supportCodeId: supportId });
      
      if (deviceData && deviceData.data) {
        setDevices(deviceData.data);
      }

      if (supportId) {
        const supportCodeData = await supportCodeController.supportCodeGetSupportCode(supportId);
        if (supportCodeData && supportCodeData.data) {
          setSupportCode(supportCodeData.data);
        } else {
          setError(t('error.supportCodeNotFound'));
        }
      } else {
        setSupportCode(null);
      }
    } catch (err) {
      setError(t('error.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [supportId, showDeleted, t, controller, supportCodeController]);

  /**
   * Opens the XML upload dialog
   */
  const openXmlUploadDialog = (): void => {
    setXmlUploadFile(null);
    setXmlUploadSupportCodeId(supportId ?? '');
    setXmlUploadCustomerDeviceId('');
    setXmlUploadError(null);
    setXmlUploadDialogOpen(true);
    
    // Load support codes for selection
    if (!supportId) {
      loadCustomerSupportCodes();
    }
  };

  /**
   * Closes the XML upload dialog
   */
  const closeXmlUploadDialog = (): void => {
    setXmlUploadDialogOpen(false);
    setXmlUploadFile(null);
    setXmlUploadSupportCodeId('');
    setXmlUploadCustomerDeviceId('');
    setXmlUploadError(null);
  };

  /**
   * Handles XML file selection
   */
  const handleXmlFileSelect = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) {
      setXmlUploadFile(file);
      setXmlUploadError(null);
    }
  };

  /**
   * Handles drag over event
   */
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * Handles drop event
   */
  const handleDrop = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.name.endsWith('.xml')) {
        setXmlUploadFile(file);
        setXmlUploadError(null);
      } else {
        setXmlUploadError(t('deviceManagement.xmlUpload.errors.invalidFileType'));
      }
    }
  };

  /**
   * Handles XML upload
   */
  const handleXmlUpload = async (): Promise<void> => {
    if (!xmlUploadFile) {
      setXmlUploadError(t('deviceManagement.xmlUpload.errors.noFile'));
      return;
    }

    setXmlUploadLoading(true);
    setXmlUploadError(null);

    try {
      const formData = new FormData();
      formData.append('xmlFile', xmlUploadFile);

      const response = await controller.deviceUploadLicenseXmlByHostId(
        { xmlFile: xmlUploadFile } as any,
        {
          supportCodeId: xmlUploadSupportCodeId || null,
          ...(xmlUploadCustomerDeviceId.trim() ? { customerDeviceId: xmlUploadCustomerDeviceId.trim() } : {}),
        } as any
      );

      if (response.data) {
        enqueueSnackbar(t('deviceManagement.xmlUpload.success'), { variant: 'success' });
        closeXmlUploadDialog();
        
        // Navigate to device page if deviceId is available
        if (response.data.deviceId) {
          navigate(`/device/${response.data.deviceId}`);
        } else {
          updateData();
        }
      }
    } catch (err: any) {
      let errorMessage = t('deviceManagement.xmlUpload.errors.uploadFailed');
      
      if (err?.response?.status === 403) {
        errorMessage = t('deviceManagement.xmlUpload.errors.noAccess');
      } else if (err?.response?.status === 400) {
        // 400 Bad Request - show detailed error message from backend
        const backendMessage = err?.response?.data?.detail || err?.response?.data || err?.message || '';
        
        // Check for specific error messages and show user-friendly translations
        if (typeof backendMessage === 'string' && 
            (backendMessage.includes('Customers must provide a supportCodeId') || 
             backendMessage.includes('as parameter or in XML'))) {
          errorMessage = t('deviceManagement.xmlUpload.errors.missingSupportCode');
        } else {
          errorMessage = backendMessage || t('deviceManagement.xmlUpload.errors.uploadFailed');
        }
      } else if (err?.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      setXmlUploadError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setXmlUploadLoading(false);
    }
  };

  /**
   * Opens the permanent delete dialog
   */
  const openPermanentDeleteDialog = (deviceId: string): void => {
    setPermanentDeleteDeviceId(deviceId);
    setPermanentDeleteDialogOpen(true);
  };

  /**
   * Closes the permanent delete dialog
   */
  const closePermanentDeleteDialog = (): void => {
    setPermanentDeleteDialogOpen(false);
    setPermanentDeleteDeviceId(null);
  };

  /**
   * Permanently deletes a device
   */
  const handlePermanentDelete = async (): Promise<void> => {
    if (!permanentDeleteDeviceId) return;

    setPermanentDeleteLoading(true);
    try {
      await controller.deviceDeleteDevice(permanentDeleteDeviceId);
      enqueueSnackbar(t('deviceManagement.permanentDelete.success'), { variant: 'success' });
      closePermanentDeleteDialog();
      updateData();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err?.message || t('deviceManagement.permanentDelete.error');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setPermanentDeleteLoading(false);
    }
  };

  /**
   * Opens the clone dialog
   */
  const openCloneDialog = (deviceId: string): void => {
    setCloneDeviceId(deviceId);
    setCloneHostId('');
    setCloneDeviceName('');
    setCloneError(null);
    setCloneDialogOpen(true);
  };

  /**
   * Closes the clone dialog
   */
  const closeCloneDialog = (): void => {
    setCloneDialogOpen(false);
    setCloneDeviceId(null);
    setCloneHostId('');
    setCloneDeviceName('');
    setCloneError(null);
  };

  /**
   * Clones a device with optional new HostID and name
   */
  const handleCloneDevice = async (): Promise<void> => {
    if (!cloneDeviceId) return;

    setCloneLoading(true);
    setCloneError(null);
    try {
      const response = await controller.deviceCloneDevice(cloneDeviceId, {
        newHostId: cloneHostId || null,
        newDeviceName: cloneDeviceName || null,
      });
      
      enqueueSnackbar(t('deviceManagement.clone.success'), { variant: 'success' });
      closeCloneDialog();
      
      // Navigate to the new cloned device
      if (response.data?.id) {
        navigate(`/device/${response.data.id}`);
      } else {
        updateData();
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err?.message || t('deviceManagement.clone.error');
      setCloneError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setCloneLoading(false);
    }
  };

  /**
   * Opens the release HostID dialog.
   * @param {string} deviceId The selected device identifier.
   */
  const openReleaseHostDialog = (deviceId: string): void => {
    setReleaseHostDeviceId(deviceId);
    setReleaseHostReason('');
    setReleaseHostError(null);
    setReleaseHostDialogOpen(true);
  };

  /**
   * Closes the release HostID dialog and resets transient state.
   */
  const closeReleaseHostDialog = (): void => {
    setReleaseHostDialogOpen(false);
    setReleaseHostDeviceId(null);
    setReleaseHostReason('');
    setReleaseHostError(null);
  };

  /**
   * Releases the HostID binding for the selected device.
   * @returns {Promise<void>} Promise resolved when the operation is complete.
   */
  const handleReleaseHostId = async (): Promise<void> => {
    if (!releaseHostDeviceId) {
      return;
    }

    setReleaseHostLoading(true);
    setReleaseHostError(null);

    try {
      await controller.deviceReleaseHostId(releaseHostDeviceId, {
        reason: releaseHostReason.trim() || null,
      });

      // Optimistisch: setze das Device auf Pending Status
      setDevices((prevDevices) =>
        prevDevices.map((device) =>
          device.id === releaseHostDeviceId ? { ...device, deviceStatus: DeviceStatusEnum.Pending } : device
        )
      );

      enqueueSnackbar(t('deviceManagement.releaseHostId.success'), { variant: 'success' });
      closeReleaseHostDialog();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err?.message || t('deviceManagement.releaseHostId.error');
      setReleaseHostError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setReleaseHostLoading(false);
    }
  };

  /**
   * Opens the Assign-Hardware-ID dialog for a pending (ordered) device.
   * @param {string} deviceId Internal device GUID.
   */
  const openAssignHostIdDialog = (deviceId: string): void => {
    setAssignHostIdDeviceId(deviceId);
    setAssignHostIdValue('');
    setAssignHostIdError(null);
    setAssignHostIdDialogOpen(true);
  };

  /**
   * Closes the Assign-Hardware-ID dialog and resets transient state.
   */
  const closeAssignHostIdDialog = (): void => {
    setAssignHostIdDialogOpen(false);
    setAssignHostIdDeviceId(null);
    setAssignHostIdValue('');
    setAssignHostIdError(null);
  };

  /**
   * Submits the one-time Hardware-ID assignment for the selected pending device.
   */
  const handleAssignHostId = async (): Promise<void> => {
    if (!assignHostIdDeviceId) {
      return;
    }

    const trimmed = assignHostIdValue.trim().toUpperCase();
    if (!/^[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/.test(trimmed)) {
      setAssignHostIdError(t('deviceManagement.assignHostId.errors.invalidFormat'));
      return;
    }

    setAssignHostIdLoading(true);
    setAssignHostIdError(null);

    try {
      await controller.deviceAssignHostId(assignHostIdDeviceId, { hostId: trimmed });
      enqueueSnackbar(t('deviceManagement.assignHostId.success'), { variant: 'success' });
      closeAssignHostIdDialog();
      updateData();
    } catch (err: any) {
      const message = err?.response?.data?.detail || err?.response?.data || err?.message;
      const errorText = typeof message === 'string' && message.length > 0
        ? message
        : t('deviceManagement.assignHostId.errors.generic');
      setAssignHostIdError(errorText);
    } finally {
      setAssignHostIdLoading(false);
    }
  };

  React.useEffect(() => {
    updateData();
  }, [supportId, updateData]);

  React.useEffect(() => {
    const storedColumns = readColumnsCookie();
    if (storedColumns) {
      setColumnVisibilityModel((prev) => ({
        ...prev,
        ...storedColumns,
      }));
    }
  }, []);

  /**
   * Loads available host systems for admin device creation.
   */
  const loadHostSystems = useCallback(async () => {
    setHostSystemsLoading(true);
    try {
      const response = await context.api.fileType.fileTypeGetHostSystems();
      setHostSystems(response.data ?? []);
    } catch (err) {
      setError(t('error.fetchFailed'));
    } finally {
      setHostSystemsLoading(false);
    }
  }, [context.api.fileType, t]);

  React.useEffect(() => {
    loadHostSystems();
  }, [loadHostSystems]);

  /**
   * Loads all support codes for assignment search.
   */
  const loadSupportCodes = useCallback(async () => {
    if (!isDevelopment) {
      return;
    }

    setSupportCodesLoading(true);
    try {
      const response = await supportCodeController.supportCodeListSupportCodes();
      setSupportCodes(response.data ?? []);
    } catch (err) {
      setError(t('error.fetchFailed'));
    } finally {
      setSupportCodesLoading(false);
    }
  }, [isDevelopment, supportCodeController, t]);

  React.useEffect(() => {
    if (assignDialogOpen && supportCodes.length === 0) {
      loadSupportCodes();
    }
  }, [assignDialogOpen, loadSupportCodes, supportCodes.length]);

  /**
   * Auto-check HostID: immediately if format XXXX-XXXX-XXXX-XXXX-XXXX is complete, otherwise 3 seconds after last input
   */
  useEffect(() => {
    // Reset validation results immediately on any input change
    setShowHostIdCheck(false);
    setHostIdCheckData(null);
    setHostIdCheckError(null);
    setHostIdCheckWaiting(false);

    if (!hostIdInputValue || hostIdInputValue.length < 5) {
      return;
    }

    // Check if format XXXX-XXXX-XXXX-XXXX-XXXX is complete (24 characters with dashes)
    const isCompleteFormat = /^[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/.test(hostIdInputValue);
    
    if (isCompleteFormat) {
      // Immediate check when format is complete
      setShowHostIdCheck(true);
      checkHostId(hostIdInputValue);
    } else {
      // Show waiting state and wait 3 seconds for incomplete format
      setHostIdCheckWaiting(true);
      setShowHostIdCheck(true);
      const timeoutId = setTimeout(() => {
        setHostIdCheckWaiting(false);
        checkHostId(hostIdInputValue);
      }, 3000);

      return () => clearTimeout(timeoutId);
    }
  }, [hostIdInputValue, checkHostId]);

  /**
   * Handles the status change of a device
   * @param {string | null | undefined} deviceId - The ID of the device
   * @param {DeviceStatusEnum} newStatus - The new status to set
   */
  const handleStatusChange = async (deviceId: string | null | undefined, newStatus: DeviceStatusEnum) => {
    if (!deviceId) {
      setError(t('error.fetchFailed'));
      return;
    }

    try {
      // Optimistisch: aktualisiere das Device lokal sofort
      setDevices((prevDevices) =>
        prevDevices.map((device) =>
          device.id === deviceId ? { ...device, deviceStatus: newStatus } : device
        )
      );

      const data: SetDeviceStatusModel = { deviceId, newStatus };
      await controller.deviceSetDeviceStatus(data);
      // Erfolg - State ist bereits aktualisiert
    } catch (err) {
      // Bei Fehler: neu laden um den richtigen Status zu zeigen
      setError(t('error.fetchFailed'));
      updateData();
    }
  };

  /**
   * Renders action buttons based on device status
   * @param {DeviceStatusEnum} status - The status of the device
   * @param {string | null | undefined} deviceId - The ID of the device
   * @returns {JSX.Element} The action buttons
   */
  const renderActionButtons = (status?: DeviceStatusEnum, deviceId?: string | null, row?: DeviceQueryModel): JSX.Element => {
    if (!deviceId) {
      return <Typography color="text.secondary">-</Typography>;
    }

    const missingHardwareId = row ? !row.hardwareIdAssigned : false;

    // Show restore and permanent delete buttons for deleted devices
    if (showDeleted && status === DeviceStatusEnum.Deleted) {
      return (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Tooltip title={t('deviceManagement.actions.restore')}>
            <IconButton color="success" onClick={() => handleStatusChange(deviceId, DeviceStatusEnum.Active)}>
              <CheckCircle />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('deviceManagement.actions.permanentDelete')}>
            <IconButton color="error" onClick={() => openPermanentDeleteDialog(deviceId)}>
              <Delete />
            </IconButton>
          </Tooltip>
        </Stack>
      );
    }

    switch (status) {
      case DeviceStatusEnum.Active:
      case DeviceStatusEnum.Pending:
        return (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <Tooltip title={t('deviceManagement.manageLicenses')}>
              <IconButton size="small" color="success" onClick={() => openLicensePage(deviceId)}>
                <VpnKey fontSize="small" />
              </IconButton>
            </Tooltip>
            {missingHardwareId && status === DeviceStatusEnum.Pending && (
              <Tooltip title={t('deviceManagement.assignHostId.actions.open')}>
                <IconButton size="small" color="primary" onClick={() => openAssignHostIdDialog(deviceId)}>
                  <Fingerprint fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {isDevelopment && (
              <>
                <Tooltip title={t('deviceManagement.assignSupportCode.actions.open')}>
                  <IconButton size="small" color="primary" onClick={() => openAssignDialog(deviceId)}>
                    <LinkIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('deviceManagement.clone.button')}>
                  <IconButton size="small" color="info" onClick={() => openCloneDialog(deviceId)}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('deviceManagement.releaseHostId.actions.open')}>
                  <IconButton size="small" color="secondary" onClick={() => openReleaseHostDialog(deviceId)}>
                    <LinkOff fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <Tooltip title={t('deviceManagement.actions.revoke')}>
              <IconButton size="small" color="warning" onClick={() => handleStatusChange(deviceId!, DeviceStatusEnum.Inactive)}>
                <Block fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      case DeviceStatusEnum.Inactive:
        return (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            {isDevelopment && (
              <>
                <Tooltip title={t('deviceManagement.assignSupportCode.actions.open')}>
                  <IconButton size="small" color="primary" onClick={() => openAssignDialog(deviceId)}>
                    <LinkIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('deviceManagement.clone.button')}>
                  <IconButton size="small" color="info" onClick={() => openCloneDialog(deviceId)}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('deviceManagement.releaseHostId.actions.open')}>
                  <IconButton size="small" color="secondary" onClick={() => openReleaseHostDialog(deviceId)}>
                    <LinkOff fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <Tooltip title={t('deviceManagement.actions.activate')}>
              <IconButton size="small" color="success" onClick={() => handleStatusChange(deviceId!, DeviceStatusEnum.Active)}>
                <CheckCircle fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('deviceManagement.actions.delete')}>
              <IconButton size="small" color="error" onClick={() => handleStatusChange(deviceId!, DeviceStatusEnum.Deleted)}>
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      default:
        return <></>;
    }
  };

  /**
   * Gets the color for status chip based on device status
   */
  const getStatusColor = (status?: DeviceStatusEnum): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case DeviceStatusEnum.Active:
        return 'success';
      case DeviceStatusEnum.Pending:
        return 'warning';
      case DeviceStatusEnum.Inactive:
        return 'error';
      default:
        return 'default';
    }
  };

  /**
   * Gets a formatted update end date based on registration date and months.
   * @param {string | null | undefined} registrationDate - The registration date.
   * @param {number | null | undefined} updatesMonths - The number of update months.
   * @returns {string} The formatted date or placeholder.
   */
  const getUpdatesUntilLabel = (registrationDate?: string | null, updatesMonths?: number | null): string => {
    if (!registrationDate || !updatesMonths) {
      return '-';
    }

    return dayjs(registrationDate).add(updatesMonths, 'month').format('DD.MM.YYYY');
  };

  /**
   * Returns the updates label: months for non-active devices, or end date for active ones.
   * @param {DeviceStatusEnum | undefined} status - The device status.
   * @param {number | null | undefined} updatesMonths - Months of updates.
   * @param {string | null | undefined} registrationDate - Registration date.
   * @returns {string} The label to display.
   */
  const getUpdatesInfoLabel = (
    status?: DeviceStatusEnum,
    updatesMonths?: number | null,
    registrationDate?: string | null,
  ): string => {
    if (status === DeviceStatusEnum.Active) {
      return getUpdatesUntilLabel(registrationDate, updatesMonths);
    }

    return updatesMonths != null ? String(updatesMonths) : '-';
  };

  /**
   * Builds data grid columns for device management.
   * @returns {GridColDef[]} Grid column definitions.
   */
  const getDeviceColumns = (): GridColDef<DeviceQueryModel>[] => {
    const columns: GridColDef<DeviceQueryModel>[] = [];

    if (search.hits) {
      columns.push({
        field: '_score',
        headerName: t('common.search'),
        width: 110,
        sortable: true,
        filterable: false,
        valueGetter: (_, row) => search.hits?.get(row.id ?? '') ?? 0,
        renderCell: (params: GridRenderCellParams<DeviceQueryModel, number>) => {
          const score = search.hits?.get(params.row.id ?? '');
          return score != null ? <SearchScoreChip score={score} dense /> : null;
        },
      });
    }

    columns.push(
      {
        field: 'deviceId',
        headerName: t('deviceManagement.columns.deviceId'),
        flex: 1,
        minWidth: 160,
        renderCell: (params: GridRenderCellParams<DeviceQueryModel, string | null | undefined>) => (
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: 'inherit' }}>
            {params.value ?? '-'}
          </Typography>
        ),
      },
      {
        field: 'customerDeviceId',
        headerName: t('deviceManagement.columns.customerDeviceId'),
        flex: 1,
        minWidth: 160,
        renderCell: (params: GridRenderCellParams<DeviceQueryModel, string | null | undefined>) => {
          const row = params.row;
          const missingHardwareId = !row.hardwareIdAssigned;
          return (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: 'inherit' }}>
                {params.value ?? '-'}
              </Typography>
              {missingHardwareId && (
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={t('deviceManagement.pendingOrdered')}
                />
              )}
            </Stack>
          );
        },
      },
      {
        field: 'name',
        headerName: t('deviceManagement.columns.name'),
        flex: 1,
        minWidth: 180,
        valueGetter: (_, row) => row.name ?? '-',
      },
      {
        field: 'hostSystemName',
        headerName: t('deviceManagement.columns.hostSystemName'),
        flex: 1,
        minWidth: 180,
        renderCell: (params: GridRenderCellParams<DeviceQueryModel, string | null | undefined>) => (
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="body2">
              {params.value ?? '-'}
            </Typography>
            {isDevelopment && (
              <Tooltip title={t('deviceManagement.updateHostSystem.actions.open')}>
                <IconButton size="small" color="primary" onClick={() => openUpdateHostDialog(params.row)}>
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
      {
        field: 'osVersion',
        headerName: t('deviceManagement.columns.osVersion'),
        flex: 1,
        minWidth: 140,
        valueGetter: (_, row) => row.osVersion ?? '-',
      },
      {
        field: 'supportCode',
        headerName: t('deviceManagement.columns.supportCode'),
        flex: 1,
        minWidth: 160,
        renderCell: (params: GridRenderCellParams<DeviceQueryModel, string | null | undefined>) =>
          params.value ? (
            <MuiLink
              component={Link}
              to={getSupportContractLink(params.value)}
              underline="hover"
              sx={{ fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: 'inherit' }}
            >
              {params.value}
            </MuiLink>
          ) : (
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: 'inherit' }}>
              -
            </Typography>
          ),
      },
      {
        field: 'customerName',
        headerName: t('deviceManagement.columns.customerName'),
        flex: 1,
        minWidth: 180,
        renderCell: (params: GridRenderCellParams<DeviceQueryModel>) => {
          if (!params.row.customerName) {
            return <Typography variant="body2">-</Typography>;
          }
          
          const linkTo = isAdmin 
            ? `/admin/support-contracts?customerId=${params.row.customerId}`
            : '/support-contracts';
          
          return (
            <MuiLink
              component={Link}
              to={linkTo}
              underline="hover"
              sx={{ color: 'inherit' }}
            >
              {params.row.customerName}
            </MuiLink>
          );
        },
      },
    );

    columns.push({
      field: 'licenseCode',
      headerName: t('deviceManagement.columns.licenseCode'),
      flex: 1,
      minWidth: 160,
      renderCell: (params: GridRenderCellParams<DeviceQueryModel, string | undefined>) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: 'inherit' }}>
          {params.value ?? '-'}
        </Typography>
      ),
    });

    columns.push(
      {
        field: 'updatesInfo',
        headerName: t('deviceManagement.columns.updatesInfo'),
        flex: 1,
        minWidth: 150,
        valueGetter: (_, row) => getUpdatesInfoLabel(row.deviceStatus, row.updatesMonths, row.registrationDate),
      },
      {
        field: 'deviceStatus',
        headerName: t('deviceManagement.columns.deviceStatus'),
        flex: 1,
        minWidth: 140,
        valueGetter: (_, row) => (row.deviceStatus ? t(`deviceManagement.status.${row.deviceStatus}`) : '-'),
      },
      {
        field: 'actions',
        headerName: t('deviceManagement.columns.actions'),
        minWidth: 280,
        width: 280,
        flex: 0,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<DeviceQueryModel>) => (
          <Box
            sx={{ display: 'flex', alignItems: 'center', height: '100%' }}
            onClick={(e) => e.stopPropagation()}
          >
            {renderActionButtons(params.row.deviceStatus, params.row.id, params.row)}
          </Box>
        ),
      },
    );

    return columns;
  };

  /**
   * Renders the data grid toolbar with columns button and quick search.
   * @returns {JSX.Element} The toolbar element.
   */
  const DeviceGridToolbar = (): JSX.Element => (
    <GridToolbarContainer sx={{ justifyContent: 'space-between' }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <GridToolbarColumnsButton />
        {isAdmin && (
          <Button
            size="small"
            onClick={() => setShowDeleted(!showDeleted)}
            startIcon={showDeleted ? <CheckCircle /> : <Block />}
            color={showDeleted ? "error" : "primary"}
          >
            {showDeleted ? t('deviceManagement.toolbar.showActive') : t('deviceManagement.toolbar.showDeleted')}
          </Button>
        )}
      </Box>
      <GridToolbarQuickFilter placeholder={t('deviceManagement.toolbar.searchPlaceholder')} debounceMs={300} />
    </GridToolbarContainer>
  );

  /**
   * Reads column visibility settings from cookies.
   * @returns {GridColumnVisibilityModel | null} The stored model or null.
   */
  const readColumnsCookie = (): GridColumnVisibilityModel | null => {
    const cookieValue = document.cookie
      .split('; ')
      .find((row) => row.startsWith('columnsDeviceManagement='))
      ?.split('=')[1];

    if (!cookieValue) {
      return null;
    }

    try {
      return JSON.parse(decodeURIComponent(cookieValue)) as GridColumnVisibilityModel;
    } catch (err) {
      return null;
    }
  };

  /**
   * Writes column visibility settings to cookies.
   * @param {GridColumnVisibilityModel} model - The visibility model to store.
   */
  const writeColumnsCookie = (model: GridColumnVisibilityModel): void => {
    const encodedValue = encodeURIComponent(JSON.stringify(model));
    document.cookie = `columnsDeviceManagement=${encodedValue}; path=/; max-age=31536000`;
  };

  /**
   * Renders mobile card view for devices
   */
  const renderMobileView = (items: DeviceQueryModel[]) => {
    if (items.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            {t('deviceManagement.noDevices') || 'Keine Geräte gefunden'}
          </Typography>
        </Box>
      );
    }

    return (
      <Stack spacing={2} sx={{ px: 2, mt: 2 }}>
        {items.map((device) => {
          const matchScore = device.id ? search.hits?.get(device.id) : undefined;
          return (
          <Card key={device.id}>
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }} noWrap>
                      {device.hostSystemName ?? '-'}
                    </Typography>
                    {isDevelopment && (
                      <Tooltip title={t('deviceManagement.updateHostSystem.actions.open')}>
                        <IconButton size="small" color="primary" onClick={() => openUpdateHostDialog(device)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', flexShrink: 0 }}>
                    {matchScore != null && <SearchScoreChip score={matchScore} dense />}
                    <Chip
                      label={device.deviceStatus ? t(`deviceManagement.status.${device.deviceStatus}`) : '-'}
                      size="small"
                      color={getStatusColor(device.deviceStatus)}
                    />
                  </Stack>
                </Stack>

                <Divider />

                <Stack spacing={1}>
                  <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('deviceManagement.columns.name')}:
                    </Typography>
                    <Typography variant="body2">
                      {device.name ?? '-'}
                    </Typography>
                  </Stack>

                  <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('deviceManagement.columns.deviceId')}:
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: 'inherit' }}>
                      {device.deviceId ?? '-'}
                    </Typography>
                  </Stack>

                  <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('deviceManagement.columns.osVersion')}:
                    </Typography>
                    <Typography variant="body2">
                      {device.osVersion ?? '-'}
                    </Typography>
                  </Stack>

                  <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('deviceManagement.columns.supportCode')}:
                    </Typography>
                    {device.supportCode ? (
                      <MuiLink
                        component={Link}
                        to={getSupportContractLink(device.supportCode)}
                        underline="hover"
                        sx={{ fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: 'inherit' }}
                      >
                        {device.supportCode}
                      </MuiLink>
                    ) : (
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: 'inherit' }}>
                        -
                      </Typography>
                    )}
                  </Stack>
                  {isDevelopment && (
                    <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('deviceManagement.columns.licenseCode')}:
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: 'inherit' }}>
                        {device.licenseCode ?? '-'}
                      </Typography>
                    </Stack>
                  )}
                  <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('deviceManagement.columns.updatesInfo')}:
                    </Typography>
                    <Typography variant="body2">
                      {getUpdatesInfoLabel(device.deviceStatus, device.updatesMonths, device.registrationDate)}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </CardContent>
            {device.deviceStatus !== DeviceStatusEnum.Deleted && (
              <CardActions>
                <Stack direction="row" spacing={1} sx={{ width: '100%', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
                  {(device.deviceStatus === DeviceStatusEnum.Pending || device.deviceStatus === DeviceStatusEnum.Active) && (
                    <Tooltip title={t('deviceManagement.manageLicenses')}>
                      <IconButton color="success" onClick={() => openLicensePage(device.id)}>
                        <VpnKey />
                      </IconButton>
                    </Tooltip>
                  )}
                  {(device.deviceStatus === DeviceStatusEnum.Pending || device.deviceStatus === DeviceStatusEnum.Active) && (
                    <Tooltip title={t('deviceManagement.actions.revoke')}>
                      <IconButton color="warning" onClick={() => handleStatusChange(device.id, DeviceStatusEnum.Inactive)}>
                        <Block />
                      </IconButton>
                    </Tooltip>
                  )}
                  {isDevelopment && (
                    <Tooltip title={t('deviceManagement.assignSupportCode.actions.open')}>
                      <IconButton color="primary" onClick={() => openAssignDialog(device.id)}>
                        <LinkIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {isDevelopment && device.id && (
                    <Tooltip title={t('deviceManagement.releaseHostId.actions.open')}>
                      <IconButton color="secondary" onClick={() => openReleaseHostDialog(device.id!)}>
                        <LinkOff />
                      </IconButton>
                    </Tooltip>
                  )}
                  {device.deviceStatus === DeviceStatusEnum.Inactive && (
                    <Tooltip title={t('deviceManagement.actions.activate')}>
                      <IconButton color="success" onClick={() => handleStatusChange(device.id, DeviceStatusEnum.Active)}>
                        <CheckCircle />
                      </IconButton>
                    </Tooltip>
                  )}
                  {device.deviceStatus === DeviceStatusEnum.Inactive && (
                    <Tooltip title={t('deviceManagement.actions.delete')}>
                      <IconButton color="error" onClick={() => handleStatusChange(device.id, DeviceStatusEnum.Deleted)}>
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </CardActions>
            )}
          </Card>
          );
        })}
      </Stack>
    );
  };

  return (
    <Container component="main" maxWidth="xl" sx={{ mb: 4 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4" component={"h1"}>{t('deviceManagement.title')}</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { xs: 'stretch', sm: 'center' }, flexWrap: 'wrap', rowGap: 1 }}>
          {isDevelopment && (
            <>
              <Button variant="outlined" color="primary" onClick={openDecodeDialog}>
                {t('deviceManagement.licenseDecode.open')}
              </Button>
              {isAdmin && (
                <Button variant="outlined" color="primary" onClick={openPublicKeyDialog}>
                  {t('deviceManagement.publicKey.open')}
                </Button>
              )}
            </>
          )}
          <Button variant="outlined" color="primary" onClick={openXmlUploadDialog} startIcon={<FontAwesomeIcon icon={faFileCode} />}>
            {t('deviceManagement.xmlUpload.button')}
          </Button>
          <Button variant="contained" color="primary" onClick={openRegisterDialog}>
            {t('deviceManagement.registerDevice.button')}
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ maxWidth: { sm: 480 }, mb: 2 }}>
        <SemanticSearchBar
          value={search.query}
          onChange={search.setQuery}
          loading={search.loading}
          unavailable={search.unavailable}
          error={search.error}
          matchCount={search.hits ? displayedDevices.length : undefined}
          placeholder={t('common.searchDevicesPlaceholder')}
        />
      </Box>
        {loading ? <Typography>{t('loading')}</Typography> : (
          <>
            {supportCode && (
              <div>
                <Typography variant="h6">
                  {t('supportCode.id')}: {' '}
                  <MuiLink
                    component={Link}
                    to={getSupportContractLink(supportCode.supportId ?? supportCode.id)}
                    underline="hover"
                    sx={{ fontFamily: 'monospace' }}
                  >
                    {supportCode.id ?? '-'}
                  </MuiLink>
                </Typography>
                <Typography variant="h6">{t('supportCode.name')}: {supportCode.name}</Typography>
              </div>
            )}
            {isMobile ? (
              renderMobileView(displayedDevices)
            ) : (
              <DataGrid
                autoHeight
                rows={displayedDevices}
                columns={getDeviceColumns()}
                getRowId={getDeviceRowId}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={(model) => {
                  setColumnVisibilityModel(model);
                  writeColumnsCookie(model);
                }}
                disableRowSelectionOnClick
                onRowClick={(params) => openLicensePage(params.row.id)}
                sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
                slots={{ toolbar: DeviceGridToolbar }}
                localeText={{
                  toolbarColumns: t('deviceManagement.toolbar.columns'),
                  toolbarColumnsLabel: t('deviceManagement.toolbar.columns'),
                }}
              />
            )}
          </>
        )}
        {error && <Typography color="error">{error}</Typography>}
        <Dialog open={registerDialogOpen} onClose={closeRegisterDialog} fullWidth maxWidth="sm">
          <DialogTitle>{t('deviceManagement.registerDevice.title')}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label={t('deviceManagement.registerDevice.fields.name')}
                value={registerData.name ?? ''}
                onChange={(event) => handleRegisterFieldChange('name', event.target.value)}
                fullWidth
              />
              <TextField
                label={t('deviceManagement.registerDevice.fields.customerDeviceId')}
                value={registerData.customerDeviceId ?? ''}
                onChange={(event) => handleRegisterFieldChange('customerDeviceId', event.target.value)}
                fullWidth
                required={!isAdmin}
                slotProps={{ htmlInput: { maxLength: 64 } }}
                helperText={t('deviceManagement.registerDevice.fields.customerDeviceIdHelper')}
              />
              {isAdmin && (
                <TextField
                  label={t('deviceManagement.registerDevice.fields.deviceId')}
                  value={registerData.deviceId ?? ''}
                  onChange={(event) => {
                    const value = event.target.value;
                    handleRegisterFieldChange('deviceId', value);
                    setHostIdInputValue(value);
                  }}
                  fullWidth
                  helperText={t('deviceManagement.registerDevice.fields.deviceIdHelper')}
                />
              )}
              {isAdmin && showHostIdCheck && (
                <Box sx={{ mt: 1 }}>
                  {(hostIdCheckLoading || hostIdCheckWaiting) ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        {t('hostIdCheck.checking')}
                      </Typography>
                    </Box>
                  ) : hostIdCheckError ? (
                    <Alert severity="error" icon={<ErrorOutline />} sx={{ py: 0.5 }}>
                      {hostIdCheckError}
                    </Alert>
                  ) : hostIdCheckData ? (
                    <Box>
                      <Alert 
                        severity={hostIdCheckData.checksum_valid ? 'success' : 'warning'} 
                        icon={hostIdCheckData.checksum_valid ? <CheckCircleOutline /> : <Warning />}
                        sx={{ py: 0.5 }}
                      >
                        <Stack spacing={0.5}>
                          <Typography variant="body2">
                            <strong>{t('hostIdCheck.checksumValid')}:</strong> {hostIdCheckData.checksum_valid ? t('yes') : t('no')}
                          </Typography>
                          {hostIdCheckData.os_type && (
                            <Typography variant="body2">
                              <strong>{t('hostIdCheck.osType')}:</strong> {hostIdCheckData.os_type}
                            </Typography>
                          )}
                          {hostIdCheckData.identifier_source && (
                            <Typography variant="body2">
                              <strong>{t('hostIdCheck.identifierSource')}:</strong> {hostIdCheckData.identifier_source}
                            </Typography>
                          )}
                          {hostIdCheckData.execution_context && (
                            <Typography variant="body2">
                              <strong>{t('hostIdCheck.executionContext')}:</strong> {hostIdCheckData.execution_context}
                            </Typography>
                          )}
                        </Stack>
                      </Alert>
                    </Box>
                  ) : null}
                </Box>
              )}
              {!isAdmin && (
                <Autocomplete
                  options={customerSupportCodes}
                  loading={customerSupportCodesLoading}
                  value={customerSupportCodes.find(code => code.id === registerData.supportCodeId) || null}
                  onChange={(_, value) => handleRegisterFieldChange('supportCodeId', value?.id ?? '')}
                  getOptionLabel={(option) => `${option.id ?? ''} ${option.name ? `- ${option.name}` : ''}`.trim()}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('deviceManagement.registerDevice.fields.supportCode')}
                      placeholder={t('deviceManagement.registerDevice.fields.searchPlaceholder')}
                      required
                      fullWidth
                    />
                  )}
                  noOptionsText={customerSupportCodesLoading ? t('loading') : t('deviceManagement.registerDevice.fields.supportCodeEmpty')}
                />
              )}
              <FormControl fullWidth required>
                <InputLabel id="device-management-hostsystem-label">
                  {t('deviceManagement.registerDevice.fields.hostSystem')}
                </InputLabel>
                <Select
                  labelId="device-management-hostsystem-label"
                  label={t('deviceManagement.registerDevice.fields.hostSystem')}
                  value={registerData.hostSystemId ?? ''}
                  onChange={(event) => handleRegisterFieldChange('hostSystemId', String(event.target.value))}
                  disabled={hostSystemsLoading}
                >
                  {hostSystemsLoading && (
                    <MenuItem value="">
                      <CircularProgress size={18} sx={{ mr: 1 }} />
                      {t('loading')}
                    </MenuItem>
                  )}
                  {!hostSystemsLoading && hostSystems.length === 0 && (
                    <MenuItem value="">
                      {t('deviceManagement.registerDevice.fields.hostSystemEmpty')}
                    </MenuItem>
                  )}
                  {hostSystems.map((hostSystem) => (
                    <MenuItem key={hostSystem.id} value={hostSystem.id ?? ''}>
                      {hostSystem.name ?? hostSystem.id ?? '-'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {isAdmin && (
                <TextField
                  label={t('deviceManagement.registerDevice.fields.updatesMonths')}
                  value={registerData.updatesMonths ?? ''}
                  onChange={(event) => handleRegisterNumberChange('updatesMonths', event.target.value)}
                  fullWidth
                  required
                  type="number"
                  slotProps={{ htmlInput: { min: 1 } }}
                />
              )}
            </Stack>
            {registerError && (
              <Typography color="error" sx={{ mt: 2 }}>
                {registerError}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeRegisterDialog} color="secondary" disabled={registerLoading}>
              {t('deviceManagement.registerDevice.actions.cancel')}
            </Button>
            <Button onClick={handleRegisterDevice} variant="contained" color="primary" disabled={registerLoading}>
              {registerLoading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  {t('loading')}
                </>
              ) : (
                t('deviceManagement.registerDevice.actions.save')
              )}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={assignDialogOpen} onClose={closeAssignDialog} fullWidth maxWidth="sm">
          <DialogTitle>{t('deviceManagement.assignSupportCode.title')}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Autocomplete
                options={supportCodes}
                loading={supportCodesLoading}
                value={selectedSupportCode}
                onChange={(_, value) => setSelectedSupportCode(value)}
                inputValue={supportSearch}
                onInputChange={(_, value) => setSupportSearch(value)}
                getOptionLabel={(option) => `${option.id ?? ''} ${option.name ? `- ${option.name}` : ''}`.trim()}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('deviceManagement.assignSupportCode.fields.search')}
                    placeholder={t('deviceManagement.assignSupportCode.fields.searchPlaceholder')}
                    fullWidth
                  />
                )}
                noOptionsText={t('deviceManagement.assignSupportCode.noResults')}
              />
            </Stack>
            {assignError && (
              <Typography color="error" sx={{ mt: 2 }}>
                {assignError}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeAssignDialog} color="secondary">
              {t('deviceManagement.assignSupportCode.actions.cancel')}
            </Button>
            <Button onClick={handleAssignSupportCode} variant="contained" color="primary" disabled={supportCodesLoading}>
              {t('deviceManagement.assignSupportCode.actions.assign')}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={updateHostDialogOpen} onClose={closeUpdateHostDialog} fullWidth maxWidth="sm">
          <DialogTitle>{t('deviceManagement.updateHostSystem.title')}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl fullWidth required>
                <InputLabel id="device-management-update-hostsystem-label">
                  {t('deviceManagement.updateHostSystem.fields.hostSystem')}
                </InputLabel>
                <Select
                  labelId="device-management-update-hostsystem-label"
                  label={t('deviceManagement.updateHostSystem.fields.hostSystem')}
                  value={updateHostSystemId}
                  onChange={(event) => setUpdateHostSystemId(String(event.target.value))}
                  disabled={hostSystemsLoading}
                >
                  {hostSystemsLoading && (
                    <MenuItem value="">
                      <CircularProgress size={18} sx={{ mr: 1 }} />
                      {t('loading')}
                    </MenuItem>
                  )}
                  {!hostSystemsLoading && hostSystems.length === 0 && (
                    <MenuItem value="">
                      {t('deviceManagement.updateHostSystem.fields.hostSystemEmpty')}
                    </MenuItem>
                  )}
                  {hostSystems.map((hostSystem) => (
                    <MenuItem key={hostSystem.id} value={hostSystem.id ?? ''}>
                      {hostSystem.name ?? hostSystem.id ?? '-'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            {updateHostError && (
              <Typography color="error" sx={{ mt: 2 }}>
                {updateHostError}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeUpdateHostDialog} color="secondary">
              {t('deviceManagement.updateHostSystem.actions.cancel')}
            </Button>
            <Button onClick={handleUpdateHostSystem} variant="contained" color="primary" disabled={hostSystemsLoading}>
              {t('deviceManagement.updateHostSystem.actions.save')}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={xmlUploadDialogOpen} onClose={closeXmlUploadDialog} fullWidth maxWidth="md">
          <DialogTitle>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <FontAwesomeIcon icon={faFileCode} />
              <Typography variant="h6">{t('deviceManagement.xmlUpload.title')}</Typography>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              {!supportId && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('deviceManagement.xmlUpload.supportCodeSection')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('deviceManagement.xmlUpload.supportCodeInfo')}
                  </Typography>
                  <Autocomplete
                    options={customerSupportCodes}
                    loading={customerSupportCodesLoading}
                    value={customerSupportCodes.find(sc => sc.id === xmlUploadSupportCodeId) || null}
                    onChange={(_, value) => setXmlUploadSupportCodeId(value?.id ?? '')}
                    getOptionLabel={(option) => `${option.id ?? ''} ${option.name ? `- ${option.name}` : ''}`.trim()}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('deviceManagement.xmlUpload.supportCode')}
                        placeholder={t('deviceManagement.xmlUpload.supportCodePlaceholder')}
                      />
                    )}
                    noOptionsText={t('deviceManagement.xmlUpload.noSupportCodes')}
                  />
                </Box>
              )}

              <Divider />

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {t('deviceManagement.xmlUpload.customerDeviceIdSection')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('deviceManagement.xmlUpload.customerDeviceIdInfo')}
                </Typography>
                <TextField
                  fullWidth
                  value={xmlUploadCustomerDeviceId}
                  onChange={(e) => setXmlUploadCustomerDeviceId(e.target.value)}
                  label={t('deviceManagement.xmlUpload.customerDeviceId')}
                  placeholder={t('deviceManagement.xmlUpload.customerDeviceIdPlaceholder')}
                  slotProps={{ htmlInput: { maxLength: 64 } }}
                />
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {t('deviceManagement.xmlUpload.fileSection')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('deviceManagement.xmlUpload.fileInfo')}
                </Typography>
                
                <input
                  ref={xmlFileInputRef}
                  type="file"
                  accept=".xml"
                  style={{ display: 'none' }}
                  onChange={handleXmlFileSelect}
                />
                
                <Box
                  onClick={() => xmlFileInputRef.current?.click()}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  sx={{
                    border: '2px dashed',
                    borderColor: xmlUploadFile ? 'success.main' : 'divider',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: xmlUploadFile ? 'success.lighter' : 'background.default',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Stack spacing={2} sx={{ alignItems: 'center' }}>
                    {xmlUploadFile ? (
                      <>
                        <CheckCircle sx={{ fontSize: 48, color: 'success.main' }} />
                        <Box>
                          <Typography variant="body1" color="success.main" sx={{ fontWeight: 600 }}>
                            {xmlUploadFile.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {(xmlUploadFile.size / 1024).toFixed(2)} KB
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          startIcon={<Upload />}
                          onClick={(e) => {
                            e.stopPropagation();
                            xmlFileInputRef.current?.click();
                          }}
                        >
                          {t('deviceManagement.xmlUpload.changeFile')}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Upload sx={{ fontSize: 48, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {t('deviceManagement.xmlUpload.dropZoneTitle')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('deviceManagement.xmlUpload.dropZoneSubtitle')}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Stack>
                </Box>
              </Box>

              {xmlUploadError && (
                <Alert severity="error">{xmlUploadError}</Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={closeXmlUploadDialog} color="secondary" disabled={xmlUploadLoading}>
              {t('deviceManagement.xmlUpload.actions.cancel')}
            </Button>
            <Button 
              onClick={handleXmlUpload} 
              variant="contained" 
              color="primary" 
              disabled={!xmlUploadFile || xmlUploadLoading}
              startIcon={xmlUploadLoading ? <CircularProgress size={20} /> : <Upload />}
            >
              {xmlUploadLoading ? t('loading') : t('deviceManagement.xmlUpload.actions.upload')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Permanent Delete Dialog */}
        <Dialog open={permanentDeleteDialogOpen} onClose={closePermanentDeleteDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{t('deviceManagement.permanentDelete.title')}</DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              {t('deviceManagement.permanentDelete.message')}
            </Typography>
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('deviceManagement.permanentDelete.warning')}
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={closePermanentDeleteDialog} disabled={permanentDeleteLoading}>
              {t('deviceManagement.permanentDelete.actions.cancel')}
            </Button>
            <Button 
              onClick={handlePermanentDelete} 
              variant="contained" 
              color="error"
              disabled={permanentDeleteLoading}
              startIcon={permanentDeleteLoading ? <CircularProgress size={20} /> : <Delete />}
            >
              {permanentDeleteLoading ? t('loading') : t('deviceManagement.permanentDelete.actions.confirm')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Clone Device Dialog */}
        <Dialog open={cloneDialogOpen} onClose={closeCloneDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{t('deviceManagement.clone.title')}</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 2 }}>
              <Alert severity="info">
                {t('deviceManagement.clone.info')}
              </Alert>

              <TextField
                fullWidth
                label={t('deviceManagement.clone.fields.hostId')}
                value={cloneHostId}
                onChange={(e) => setCloneHostId(e.target.value)}
                placeholder="XXXX-XXXX-XXXX-XXXX-XXXX"
                helperText={t('deviceManagement.clone.fields.hostIdHelper')}
                disabled={cloneLoading}
              />

              <TextField
                fullWidth
                label={t('deviceManagement.clone.fields.deviceName')}
                value={cloneDeviceName}
                onChange={(e) => setCloneDeviceName(e.target.value)}
                helperText={t('deviceManagement.clone.fields.deviceNameHelper')}
                disabled={cloneLoading}
              />

              {cloneError && (
                <Alert severity="error">{cloneError}</Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeCloneDialog} disabled={cloneLoading}>
              {t('deviceManagement.clone.actions.cancel')}
            </Button>
            <Button 
              onClick={handleCloneDevice} 
              variant="contained" 
              color="primary"
              disabled={cloneLoading}
              startIcon={cloneLoading ? <CircularProgress size={20} /> : <ContentCopy />}
            >
              {cloneLoading ? t('loading') : t('deviceManagement.clone.actions.confirm')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Release HostID Dialog */}
        <Dialog open={releaseHostDialogOpen} onClose={closeReleaseHostDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{t('deviceManagement.releaseHostId.title')}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="warning">{t('deviceManagement.releaseHostId.warning')}</Alert>
              <Typography variant="body2" color="text.secondary">
                {t('deviceManagement.releaseHostId.info')}
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label={t('deviceManagement.releaseHostId.fields.reason')}
                helperText={t('deviceManagement.releaseHostId.fields.reasonHelper')}
                value={releaseHostReason}
                onChange={(event) => setReleaseHostReason(event.target.value)}
                disabled={releaseHostLoading}
              />
              {releaseHostError && <Alert severity="error">{releaseHostError}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeReleaseHostDialog} disabled={releaseHostLoading}>
              {t('deviceManagement.releaseHostId.actions.cancel')}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleReleaseHostId}
              disabled={releaseHostLoading}
              startIcon={releaseHostLoading ? <CircularProgress size={20} /> : <LinkOff />}
            >
              {releaseHostLoading ? t('loading') : t('deviceManagement.releaseHostId.actions.confirm')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={assignHostIdDialogOpen} onClose={closeAssignHostIdDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{t('deviceManagement.assignHostId.title')}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="info">{t('deviceManagement.assignHostId.info')}</Alert>
              <TextField
                fullWidth
                autoFocus
                label={t('deviceManagement.assignHostId.fields.hostId')}
                placeholder="XXXX-XXXX-XXXX-XXXX-XXXX"
                value={assignHostIdValue}
                onChange={(event) => setAssignHostIdValue(event.target.value)}
                disabled={assignHostIdLoading}
                helperText={t('deviceManagement.assignHostId.fields.hostIdHelper')}
                slotProps={{ htmlInput: { style: { fontFamily: 'monospace', textTransform: 'uppercase' } } }}
              />
              {assignHostIdError && <Alert severity="error">{assignHostIdError}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeAssignHostIdDialog} disabled={assignHostIdLoading}>
              {t('deviceManagement.assignHostId.actions.cancel')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAssignHostId}
              disabled={assignHostIdLoading || !assignHostIdValue.trim()}
              startIcon={assignHostIdLoading ? <CircularProgress size={20} /> : <VpnKey />}
            >
              {assignHostIdLoading ? t('loading') : t('deviceManagement.assignHostId.actions.confirm')}
            </Button>
          </DialogActions>
        </Dialog>

        <DeviceLicenseDecodeDialog open={decodeDialogOpen} onClose={closeDecodeDialog} />
        <DeviceLicensePublicKeyDialog open={publicKeyDialogOpen} onClose={closePublicKeyDialog} />
    </Container>
  );
};

export default DeviceManagement;
